import { useEffect, useRef } from "react";
import * as THREE from "three";

/**
 * Live 3D "slice of forest" for the Silviculture Lab.
 *
 * Renders the treated stand only, driven by the same targets the treatment
 * sends to the sim's fuel models:
 *
 *   thinningPct -> removes stems (drives elevated fuel + CBD); felled stems
 *                  lie on the ground as slash unless removeSlash is set
 *   cbhRaiseM   -> raises the canopy base (longer bare bole, CBH)
 *
 * Representative baseline canopy values stand in for the untreated stand
 * because the treatment is not tied to one polygon's data; the geometry
 * mapping matches FuelLoad3DTrees so the preview reads like the map's 3D
 * trees. Drag to orbit, scroll to zoom.
 */

export type StandShape = "conifer" | "broadleaf";

interface SilviculturePreviewProps {
  cbhRaiseM: number;
  thinningPct: number;
  removeSlash: boolean;
  shape?: StandShape;
  height?: number;
}

// Representative baseline stand (metres / kg·m⁻³). Shared with the lab readouts.
export const BASE_CH = 22;
export const BASE_CBH = 3;
export const BASE_CBD = 0.18;

export const cbdNorm = (cbd: number) =>
  Math.min(1, Math.max(0, (cbd - 0.03) / 0.22));

const clamp = (v: number, a: number, b: number) => Math.min(b, Math.max(a, v));

// Pruning keeps at least this fraction of canopy height as live crown — must
// match the sim's BaseFuelModel._MIN_LIVE_CROWN_FRAC.
const MIN_LIVE_CROWN_FRAC = 0.2;

/** Baseline → treated canopy structure on the representative stand. */
export function silvicultureResult(cbhRaiseM: number, thinningPct: number) {
  const thinFrac = clamp(thinningPct, 0, 100) / 100;
  const afterCH = BASE_CH;
  const afterCBH = Math.min(
    BASE_CBH + Math.max(cbhRaiseM, 0),
    afterCH * (1 - MIN_LIVE_CROWN_FRAC)
  );
  const afterCBD = BASE_CBD * (1 - thinFrac);
  return { afterCH, afterCBH, afterCBD };
}

// Deterministic per-tree variation so the stand doesn't reshuffle on rebuild.
const hash01 = (i: number, salt: number) => {
  let x = Math.imul(i + 1, 0x9e3779b1) ^ Math.imul(salt + 1, 0x85ebca6b);
  x ^= x >>> 15;
  x = Math.imul(x, 0x2c1b3c6d);
  x ^= x >>> 12;
  return (x >>> 0) / 4294967296;
};

const trunkGeo = new THREE.CylinderGeometry(0.5, 0.62, 1, 7).translate(0, 0.5, 0);
const coneGeo = new THREE.ConeGeometry(0.5, 1, 9).translate(0, 0.5, 0);
const blobGeo = new THREE.SphereGeometry(0.5, 10, 8).translate(0, 0.5, 0);
const stumpGeo = new THREE.CylinderGeometry(0.55, 0.66, 1, 7).translate(0, 0.5, 0);

const CONE_LO = new THREE.Color("#9cc08a");
const CONE_HI = new THREE.Color("#11502a");
const BLOB_LO = new THREE.Color("#b5d18b");
const BLOB_HI = new THREE.Color("#1f5a2c");
const TRUNK = new THREE.Color("#6b4a32");
const STUMP = new THREE.Color("#8a6845");
const LOG = new THREE.Color("#7a5a3c");
const DRIED_CROWN = new THREE.Color("#8c7a4e");

function buildTree(
  ch: number,
  cbh: number,
  cbd: number,
  shape: StandShape,
  hueShift: number
): THREE.Group {
  const grp = new THREE.Group();
  const cbdN = cbdNorm(cbd);
  const baseH = clamp(cbh, 0, ch * 0.92);
  const crownLen = Math.max(ch - baseH, 0.4);
  const broadleaf = shape === "broadleaf";
  const crownR = broadleaf
    ? Math.max(1.0, crownLen * 0.5 * (0.6 + 0.85 * cbdN))
    : Math.max(0.7, crownLen * 0.26 * (0.6 + 0.9 * cbdN));
  const trunkR = 0.13 + 0.014 * ch;

  const trunkMat = new THREE.MeshLambertMaterial({ color: TRUNK });
  const trunk = new THREE.Mesh(trunkGeo, trunkMat);
  trunk.scale.set(trunkR * 2, baseH + crownLen * (broadleaf ? 0.3 : 0.2), trunkR * 2);
  trunk.castShadow = true;
  grp.add(trunk);

  const crownCol = (broadleaf ? BLOB_LO : CONE_LO)
    .clone()
    .lerp(broadleaf ? BLOB_HI : CONE_HI, cbdN);
  crownCol.offsetHSL(hueShift * 0.05 - 0.025, 0, hueShift * 0.1 - 0.05);
  const crownMat = new THREE.MeshLambertMaterial({ color: crownCol });

  if (broadleaf) {
    const crown = new THREE.Mesh(blobGeo, crownMat);
    crown.scale.set(crownR * 2, crownLen, crownR * 1.9);
    crown.position.y = baseH;
    crown.castShadow = true;
    grp.add(crown);
  } else {
    const lower = new THREE.Mesh(coneGeo, crownMat);
    lower.scale.set(crownR * 2, crownLen * 0.72, crownR * 2);
    lower.position.y = baseH;
    lower.castShadow = true;
    grp.add(lower);
    const upper = new THREE.Mesh(coneGeo, crownMat);
    upper.scale.set(crownR * 1.24, crownLen * 0.56, crownR * 1.24);
    upper.position.y = baseH + crownLen * 0.42;
    upper.castShadow = true;
    grp.add(upper);
  }

  return grp;
}

function buildStump(ch: number): THREE.Mesh {
  const trunkR = 0.13 + 0.014 * ch;
  const stump = new THREE.Mesh(
    stumpGeo,
    new THREE.MeshLambertMaterial({ color: STUMP })
  );
  stump.scale.set(trunkR * 2.2, 0.35, trunkR * 2.2);
  stump.castShadow = true;
  return stump;
}

/** Felled stem lying on the ground next to its stump (slash not removed). */
function buildLog(ch: number, dirAngle: number): THREE.Group {
  const grp = new THREE.Group();
  const trunkR = 0.13 + 0.014 * ch;
  const len = ch * 0.55;
  const log = new THREE.Mesh(
    trunkGeo,
    new THREE.MeshLambertMaterial({ color: LOG })
  );
  log.scale.set(trunkR * 2, len, trunkR * 2);
  log.rotation.z = -Math.PI / 2; // base-at-origin +y geometry → lies along +x
  log.position.set(0.7, trunkR, 0);
  log.castShadow = true;
  grp.add(log);
  // Dried crown wad at the felled top.
  const wad = new THREE.Mesh(
    blobGeo,
    new THREE.MeshLambertMaterial({ color: DRIED_CROWN })
  );
  const wadR = Math.max(0.6, ch * 0.07);
  wad.scale.set(wadR * 2.4, wadR * 1.1, wadR * 1.8);
  wad.position.set(0.7 + len, 0, 0);
  wad.castShadow = true;
  grp.add(wad);
  grp.rotation.y = dirAngle;
  return grp;
}

const SilviculturePreview: React.FC<SilviculturePreviewProps> = ({
  cbhRaiseM,
  thinningPct,
  removeSlash,
  shape = "conifer",
  height = 230,
}) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const standRef = useRef<THREE.Group | null>(null);
  const frameRef = useRef<number | null>(null);
  const yawRef = useRef(0.6);
  const pitchRef = useRef(0.42);
  const radiusRef = useRef(58);
  const autoRotateRef = useRef(true);

  // One-time scene setup + orbit interaction.
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.domElement.style.cursor = "grab";
    renderer.domElement.style.touchAction = "none";
    wrap.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    const scene = new THREE.Scene();

    const camera = new THREE.PerspectiveCamera(42, 1, 0.1, 400);

    scene.add(new THREE.HemisphereLight(0xffffff, 0x404034, 1.0));
    const sun = new THREE.DirectionalLight(0xfff1d6, 1.5);
    sun.position.set(-26, 40, 18);
    sun.castShadow = true;
    sun.shadow.mapSize.set(1024, 1024);
    sun.shadow.camera.left = -36;
    sun.shadow.camera.right = 36;
    sun.shadow.camera.top = 36;
    sun.shadow.camera.bottom = -36;
    scene.add(sun);

    const ground = new THREE.Mesh(
      new THREE.CircleGeometry(36, 48),
      new THREE.MeshLambertMaterial({ color: 0x2f3a28 })
    );
    ground.rotation.x = -Math.PI / 2;
    ground.receiveShadow = true;
    scene.add(ground);

    const stand = new THREE.Group();
    scene.add(stand);
    standRef.current = stand;

    const resize = () => {
      const w = wrap.clientWidth || 1;
      const h = wrap.clientHeight || 1;
      // Let three style the canvas to CSS pixels — with the style update
      // skipped, the 2× pixel-ratio backing store displays at double size and
      // pushes the scene centre off toward the corner.
      renderer.setSize(w, h);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(wrap);

    // Drag to orbit (yaw + pitch), scroll to zoom. Auto-rotate until first drag.
    let dragging = false;
    let lastX = 0;
    let lastY = 0;
    const el = renderer.domElement;
    const onPointerDown = (e: PointerEvent) => {
      dragging = true;
      autoRotateRef.current = false;
      lastX = e.clientX;
      lastY = e.clientY;
      el.setPointerCapture(e.pointerId);
      el.style.cursor = "grabbing";
    };
    const onPointerMove = (e: PointerEvent) => {
      if (!dragging) return;
      yawRef.current -= (e.clientX - lastX) * 0.006;
      pitchRef.current = clamp(
        pitchRef.current + (e.clientY - lastY) * 0.004,
        0.08,
        1.3
      );
      lastX = e.clientX;
      lastY = e.clientY;
    };
    const onPointerUp = (e: PointerEvent) => {
      dragging = false;
      el.style.cursor = "grab";
      if (el.hasPointerCapture(e.pointerId)) el.releasePointerCapture(e.pointerId);
    };
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      radiusRef.current = clamp(radiusRef.current * (1 + e.deltaY * 0.0012), 26, 110);
    };
    el.addEventListener("pointerdown", onPointerDown);
    el.addEventListener("pointermove", onPointerMove);
    el.addEventListener("pointerup", onPointerUp);
    el.addEventListener("pointercancel", onPointerUp);
    el.addEventListener("wheel", onWheel, { passive: false });

    const animate = () => {
      if (autoRotateRef.current) yawRef.current += 0.0035;
      const r = radiusRef.current;
      const pitch = pitchRef.current;
      camera.position.set(
        Math.cos(yawRef.current) * r * Math.cos(pitch),
        r * Math.sin(pitch),
        Math.sin(yawRef.current) * r * Math.cos(pitch)
      );
      camera.lookAt(0, 7, 0);
      renderer.render(scene, camera);
      frameRef.current = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      if (frameRef.current != null) cancelAnimationFrame(frameRef.current);
      ro.disconnect();
      el.removeEventListener("pointerdown", onPointerDown);
      el.removeEventListener("pointermove", onPointerMove);
      el.removeEventListener("pointerup", onPointerUp);
      el.removeEventListener("pointercancel", onPointerUp);
      el.removeEventListener("wheel", onWheel);
      renderer.dispose();
      if (el.parentNode === wrap) wrap.removeChild(el);
      rendererRef.current = null;
    };
  }, []);

  // Rebuild the stand whenever the treatment parameters or shape change.
  useEffect(() => {
    const stand = standRef.current;
    if (!stand) return;

    while (stand.children.length) {
      const child = stand.children.pop() as THREE.Object3D;
      child.traverse((o) => {
        const mesh = o as THREE.Mesh;
        if (mesh.isMesh) (mesh.material as THREE.Material).dispose();
      });
    }

    const { afterCH, afterCBH } = silvicultureResult(cbhRaiseM, thinningPct);

    // Jittered planting grid; thinning removes stems (stumps remain) rather
    // than narrowing crowns — remaining trees keep their baseline crown, the
    // stand-level CBD drop comes from fewer stems.
    const broadleaf = shape === "broadleaf";
    const cols = broadleaf ? 5 : 6;
    const rows = broadleaf ? 4 : 5;
    const spacing = broadleaf ? 7.2 : 5.4;
    const n = cols * rows;
    const thinFrac = clamp(thinningPct, 0, 100) / 100;
    const removed = Math.round(n * thinFrac);
    const removalOrder = Array.from({ length: n }, (_, i) => i).sort(
      (a, b) => hash01(a, 7) - hash01(b, 7)
    );
    const removedSet = new Set(removalOrder.slice(0, removed));

    for (let i = 0; i < n; i++) {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const x =
        (col - (cols - 1) / 2) * spacing + (hash01(i, 1) - 0.5) * spacing * 0.7;
      const z =
        (row - (rows - 1) / 2) * spacing + (hash01(i, 2) - 0.5) * spacing * 0.7;
      const jitter = 0.82 + hash01(i, 3) * 0.36;

      if (removedSet.has(i)) {
        const stump = buildStump(afterCH * jitter);
        stump.position.set(x, 0, z);
        stand.add(stump);
        if (!removeSlash) {
          const log = buildLog(afterCH * jitter, hash01(i, 6) * Math.PI * 2);
          log.position.set(x, 0, z);
          stand.add(log);
        }
        continue;
      }

      const t = buildTree(afterCH * jitter, afterCBH, BASE_CBD, shape, hash01(i, 4));
      t.position.set(x, 0, z);
      t.rotation.y = hash01(i, 5) * Math.PI * 2;
      stand.add(t);
    }
  }, [cbhRaiseM, thinningPct, removeSlash, shape]);

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height,
        borderRadius: 8,
        overflow: "hidden",
        background:
          "linear-gradient(180deg, #20283a 0%, #1a2030 55%, #161c28 100%)",
        border: "1px solid rgba(255,255,255,0.08)",
      }}
    >
      <div ref={wrapRef} style={{ position: "absolute", inset: 0 }} />
      <div
        style={{
          position: "absolute",
          bottom: 8,
          right: 12,
          fontSize: 10,
          letterSpacing: 0.4,
          color: "rgba(255,255,255,0.4)",
          pointerEvents: "none",
          textTransform: "uppercase",
        }}
      >
        drag to orbit · scroll to zoom
      </div>
    </div>
  );
};

export default SilviculturePreview;
